import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "~/services/api";
import { FullGridContext } from ".";

const DEBOUNCE_MS = 450;
const ROWS_PER_PAGE = 10;

const normalize = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export default function DSODashboardDetailsModal({ isOpen, onClose, context }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");

  const tableContainerRef = useRef(null);
  const navigate = useNavigate();
  const { handleOpened } = useContext(FullGridContext);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedTerm((searchTerm || "").trim()),
      DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!isOpen) {
      setRows([]);
      setError(null);
      setLoading(false);
      setSearchTerm("");
      setDebouncedTerm("");
      return;
    }
    if (!context?.section || !context?.type) return;

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      setRows([]);
      try {
        const params = {
          section: context.section,
          type: context.type,
        };
        if (context.status) {
          params.status = context.status;
        }
        if (context.filialId) {
          params.filial_id = context.filialId;
        }
        if (context.startDate) {
          params.start_date = context.startDate;
        }
        if (context.endDate) {
          params.end_date = context.endDate;
        }
        const { data } = await api.get("/dso-dashboard/details", { params });
        setRows(data?.rows || []);
        setCurrentPage(1);
      } catch {
        setError("Failed to load details.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [isOpen, context?.section, context?.status, context?.type]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedTerm]);

  const filteredRows = useMemo(() => {
    const term = normalize(debouncedTerm);
    if (!term) return rows;
    return rows.filter((row) => {
      return (
        normalize(row.full_name).includes(term) ||
        normalize(row.registration_number).includes(term) ||
        normalize(row.agent_name).includes(term)
      );
    });
  }, [rows, debouncedTerm]);

  const paginated = useMemo(() => {
    const total = filteredRows.length;
    const pages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
    const safePage = Math.min(Math.max(1, currentPage), pages);
    const start = (safePage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return { total, pages, page: safePage, rows: filteredRows.slice(start, end) };
  }, [filteredRows, currentPage]);

  useEffect(() => {
    if (currentPage > paginated.pages && paginated.pages > 0) {
      setCurrentPage(paginated.pages);
    }
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  }, [paginated.pages]);

  const handlePageChange = (newPage) => {
    setCurrentPage((prev) => {
      if (!Number.isFinite(newPage)) return prev;
      return Math.max(1, Math.min(newPage, paginated.pages));
    });
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  };

  if (!isOpen) return null;

  const getRouteForSection = (section) => {
    if (section === "onboarding") return "/DSO/Onboard Pendings";
    if (section === "pre_arrivals") return "/DSO/PreArrivals";
    if (section === "arrivals") return "/DSO/Arrivals";
    return null;
  };

  const getRowIdForSection = (row) => {
    if (
      context?.section === "pre_arrivals" ||
      context?.section === "arrivals" ||
      context?.section === "orientation"
    ) {
      return row.prearrival_id;
    }
    if (context?.section === "onboarding") {
      return row.enrollment_id;
    }
    return null;
  };

  const handleView = (row) => {
    const route = getRouteForSection(context?.section);
    const id = getRowIdForSection(row);
    if (!route || !id) return;
    if (handleOpened) handleOpened(String(id));
    navigate(route);
    onClose();
  };

  const totalRowsUI = paginated.total;
  const totalPages = paginated.pages;
  const currentPageSafe = paginated.page;
  const startRecord = totalRowsUI ? (currentPageSafe - 1) * ROWS_PER_PAGE + 1 : 0;
  const endRecord = totalRowsUI
    ? Math.min(currentPageSafe * ROWS_PER_PAGE, totalRowsUI)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,.75)" }}
    >
      <div
        className={`bg-white rounded-lg shadow-xl ${
          isMobile ? "w-full h-full" : "w-full max-w-7xl max-h-[90vh]"
        } flex flex-col overflow-hidden`}
      >
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex-1">
            <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>
              DSO Details - {context?.label || "Details"}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Section: {context?.section || "-"} | Status: {context?.status || "-"} | Type:{" "}
              {context?.type || "-"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              {totalRowsUI > 0 && (
                <>
                  <span>
                    Showing <span className="font-semibold">{startRecord}</span> to{" "}
                    <span className="font-semibold">{endRecord}</span> of{" "}
                    <span className="font-semibold">{totalRowsUI}</span> rows
                  </span>
                  <span className="text-gray-400">|</span>
                  <span>Page</span>
                  <select
                    value={currentPageSafe}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                    disabled={loading || totalPages <= 1}
                  >
                    {Array.from({ length: totalPages || 1 }, (_, i) => i + 1).map(
                      (page) => (
                        <option key={page} value={page}>
                          {page}
                        </option>
                      )
                    )}
                  </select>
                  <span>of {totalPages}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, registration, agent..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-96 pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                  />
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={tableContainerRef} className="flex-1 overflow-auto min-h-0">
          <table className="w-full min-w-[1200px] border-collapse">
            <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-3 text-sm font-semibold text-gray-700">
                  Full Name
                </th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">
                  Code
                </th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">
                  Agent
                </th>
                <th className="text-center p-3 text-sm font-semibold text-gray-700">
                  View
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="animate-spin" size={18} />
                      Loading details...
                    </div>
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && paginated.rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-gray-500">
                    No data available.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                paginated.rows.map((row, idx) => (
                  <tr
                    key={`${getRowIdForSection(row) || row.enrollment_id || row.student_id || idx}`}
                    className={`border-b transition-colors ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-orange-50`}
                  >
                    <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.full_name || "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.registration_number || "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                      {row.agent_name || "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleView(row)}
                        disabled={!getRowIdForSection(row)}
                        className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:border-mila_orange hover:text-mila_orange disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {totalRowsUI > 0 && (
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 flex-shrink-0">
            <div className="text-sm text-gray-600">
              Page {currentPageSafe} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPageSafe - 1)}
                disabled={currentPageSafe === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {currentPageSafe} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPageSafe + 1)}
                disabled={currentPageSafe === totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
